/**
 * AutoUncle's `is_not_imported` segmentation flag.
 *
 * AutoUncle defaults every car to "imported", so a Swedish-market car that does not say
 * otherwise is matched against the wrong comparables. The flag is a MATCHING field, not a
 * price adjustment.
 *
 * Two rules, and the distinction matters:
 *
 *  - LIVE lookup: use the provider's raw tri-state as-is. It can express all three states
 *    (not imported / imported / unknown).
 *  - STORED data: only an explicit "Sverige" origin may assert "true". Never assert
 *    "false", because `origin` is lossy - it collapses "imported" and "unknown" into the
 *    same empty value, so an absent origin cannot be read as "imported".
 *
 * Mirrors the backend helper `deriveIsNotImportedFromOrigin` in autoUncleMappers.ts.
 */

/** Derive the flag from a STORED origin value. Never returns "false". */
export const deriveIsNotImportedFromOrigin = (
  origin?: string | null
): string | undefined =>
  typeof origin === "string" && origin.trim().toLowerCase() === "sverige"
    ? "true"
    : undefined;

/**
 * Resolve the flag for an estimation request.
 *
 * Prefers the raw tri-state carried from the lookup; falls back to deriving it from a
 * stored origin. Returns undefined when neither can establish the value, which omits the
 * parameter entirely (AutoUncle treats an explicit "false" as identical to omission).
 */
export const resolveIsNotImported = (
  rawFlag?: string | null,
  origin?: string | null
): string | undefined => {
  if (typeof rawFlag === "string" && rawFlag.trim() !== "") return rawFlag;
  return deriveIsNotImportedFromOrigin(origin);
};
